'use client';

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';

import { useAuth } from './auth-provider';

import { useNavigation } from '@/components/navigation/navigation-context';
import { RatingModal } from '@/components/order/rating-modal';
import { RatingSuccessDialog } from '@/components/order/rating-success-dialog';
import { useOrderRating } from '@/hooks/use-order-rating';

interface OrderDetail {
  code: string;
  store_name: string;
  store_image_url?: string;
}

interface RatingContextType {
  openRatingModal: (orderDetail: OrderDetail) => void;
  openCommentModal: (
    orderDetail: OrderDetail,
    existingRating: number,
    initialData?: { comment?: string; reasons?: string[]; images?: string[] }
  ) => void;
  closeRatingModal: () => void;
  checkRatingOnCityPage: () => void;
  isOpen: boolean;
  currentOrder: OrderDetail | null;
}

const RatingContext = createContext<RatingContextType | undefined>(undefined);

interface RatingProviderProps {
  children: ReactNode;
}

export function RatingProvider({ children }: RatingProviderProps) {
  const { user, isAuthenticated } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [currentOrder, setCurrentOrder] = useState<OrderDetail | null>(null);
  const [modalStep, setModalStep] = useState<1 | 2>(1);
  const [existingRating, setExistingRating] = useState<number | undefined>();
  const [hideImageUpload, setHideImageUpload] = useState(false);
  const [initialData, setInitialData] = useState<
    | {
        comment?: string;
        reasons?: string[];
        images?: string[];
      }
    | undefined
  >();
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const { setForceHidden } = useNavigation();

  const {
    pendingRatingOrder,
    showRatingModal,
    setShowRatingModal,
    markOrderAsRated,
    checkRatingOnCityPage,
  } = useOrderRating({ user, isAuthenticated });

  const openRatingModal = useCallback(
    (orderDetail: OrderDetail) => {
      setCurrentOrder(orderDetail);
      setModalStep(1);
      setExistingRating(undefined);
      setHideImageUpload(false); // Show image upload for regular rating modal
      setIsOpen(true);
      setForceHidden(true); // Hide navigation when rating modal opens
    },
    [setForceHidden]
  );

  const openCommentModal = useCallback(
    (
      orderDetail: OrderDetail,
      rating: number,
      initialDataParam?: {
        comment?: string;
        reasons?: string[];
        images?: string[];
      }
    ) => {
      setCurrentOrder(orderDetail);
      setModalStep(2);
      setExistingRating(rating);
      setInitialData(initialDataParam);
      setHideImageUpload(true); // Hide image upload for comment-only modal
      setIsOpen(true);
      setForceHidden(true); // Hide navigation when rating modal opens
    },
    [setForceHidden]
  );

  const closeRatingModal = useCallback(() => {
    setIsOpen(false);
    setForceHidden(false); // Show navigation when rating modal closes
    // Clear current order after animation
    setTimeout(() => {
      setCurrentOrder(null);
      setModalStep(1);
      setExistingRating(undefined);
      setInitialData(undefined);
      setHideImageUpload(false);
    }, 300);
  }, [setForceHidden]);

  const handleShowSuccessDialog = useCallback(() => {
    // Don't show success dialog if in edit mode (has initialData)
    if (!initialData) {
      setShowSuccessDialog(true);
    }
  }, [initialData]);

  const handleAutomaticShowSuccessDialog = useCallback(() => {
    setShowSuccessDialog(true);
  }, []);

  const handleRatingComplete = useCallback(() => {
    markOrderAsRated();
    closeRatingModal();
    // Don't show success dialog if in edit mode (has initialData)
    if (!initialData) {
      setShowSuccessDialog(true);
    }
  }, [markOrderAsRated, closeRatingModal, initialData]);

  const handleModalOpenChange = (open: boolean) => {
    if (!open) {
      closeRatingModal();
    }
  };

  const handleAutomaticModalOpenChange = (open: boolean) => {
    if (!open) {
      setForceHidden(false); // Show navigation when automatic modal closes
    }
    setShowRatingModal(open);
  };

  // Handle automatic rating modal (from useOrderRating hook)
  const handleAutomaticRatingComplete = useCallback(() => {
    markOrderAsRated();
    setForceHidden(false); // Show navigation when automatic rating modal closes
    setShowSuccessDialog(true);
  }, [markOrderAsRated, setForceHidden]);

  // Handle automatic rating modal navigation hiding
  useEffect(() => {
    if (showRatingModal && pendingRatingOrder && !currentOrder) {
      setForceHidden(true); // Hide navigation when automatic rating modal opens
    } else if (!showRatingModal && !isOpen) {
      setForceHidden(false); // Show navigation when all modals are closed
    }
  }, [
    showRatingModal,
    pendingRatingOrder,
    currentOrder,
    isOpen,
    setForceHidden,
  ]);

  const value: RatingContextType = {
    openRatingModal,
    openCommentModal,
    closeRatingModal,
    checkRatingOnCityPage,
    isOpen,
    currentOrder,
  };

  return (
    <RatingContext.Provider value={value}>
      {children}

      {/* Manual rating modal (opened via context) */}
      {currentOrder && (
        <RatingModal
          open={isOpen}
          onOpenChange={handleModalOpenChange}
          onSubmitRating={handleRatingComplete}
          onShowSuccessDialog={handleShowSuccessDialog}
          orderDetail={currentOrder}
          initialStep={modalStep}
          existingRating={existingRating}
          hideImageUpload={hideImageUpload}
          initialData={initialData}
        />
      )}

      {/* Automatic rating modal (from useOrderRating hook) */}
      {pendingRatingOrder && !currentOrder && (
        <RatingModal
          open={showRatingModal}
          onOpenChange={handleAutomaticModalOpenChange}
          onSubmitRating={handleAutomaticRatingComplete}
          onShowSuccessDialog={handleAutomaticShowSuccessDialog}
          orderDetail={{
            code: pendingRatingOrder.code,
            store_name: pendingRatingOrder.store_name,
            store_image_url: pendingRatingOrder.store_image_url,
          }}
        />
      )}

      {/* Success Dialog */}
      <RatingSuccessDialog
        open={showSuccessDialog}
        onOpenChange={setShowSuccessDialog}
      />
    </RatingContext.Provider>
  );
}

export function useRating(): RatingContextType {
  const context = useContext(RatingContext);
  if (context === undefined) {
    throw new Error('useRating must be used within a RatingProvider');
  }
  return context;
}
